import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { PageHeader } from '../components/ui/PageHeader';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { apiService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

interface SubscriptionPlan {
  id: number;
  name: string;
  description: string;
  price: number;
  intCoins: number;
  durationDays: number;
  intCoinsExpirationDays: number;
  features: string[];
  status: 'active' | 'inactive';
}

interface UserSubscription {
  id: number;
  plan: SubscriptionPlan;
  startDate: string;
  endDate: string;
  status: 'active' | 'expired' | 'cancelled';
  daysRemaining: number;
  isActive: boolean;
}

interface PaymentMethod {
  id: number;
  name: string;
  displayName: string;
  description: string;
  isOnline: boolean;
  isActive: boolean;
}

interface OfflinePaymentFormData {
  referenceNumber: string;
  bankDetails: string;
  paymentInstructions: string;
  paymentProof: File | null;
}

export const SubscriptionPlansPage: React.FC = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [currentSubscription, setCurrentSubscription] = useState<UserSubscription | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingPayment, setProcessingPayment] = useState<number | null>(null);
  
  // Offline payment modal state
  const [showOfflineModal, setShowOfflineModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null);
  const [showPaymentMethodSelection, setShowPaymentMethodSelection] = useState(true);
  const [offlineFormData, setOfflineFormData] = useState<OfflinePaymentFormData>({
    referenceNumber: '',
    bankDetails: '',
    paymentInstructions: '',
    paymentProof: null
  });
  const [submittingOffline, setSubmittingOffline] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get subscription plans and payment methods
      const [plansData, paymentMethodsData] = await Promise.all([
        apiService.getSubscriptionPlans(),
        apiService.getPaymentMethods()
      ]);
      
      setPlans(plansData);
      
      // Map snake_case to camelCase for payment methods
      // Ensure paymentMethodsData is an array (handle paginated or direct array responses)
      let methodsArray: any[] = [];
      if (Array.isArray(paymentMethodsData)) {
        methodsArray = paymentMethodsData;
      } else if (paymentMethodsData && typeof paymentMethodsData === 'object' && 'results' in paymentMethodsData) {
        methodsArray = Array.isArray((paymentMethodsData as any).results) ? (paymentMethodsData as any).results : [];
      }
      
      const mappedPaymentMethods = methodsArray.map((method: any) => ({
        id: method.id,
        name: method.name,
        displayName: method.display_name || method.displayName,
        description: method.description,
        isOnline: method.is_online ?? method.isOnline,
        isActive: method.is_active ?? method.isActive
      }));
      setPaymentMethods(mappedPaymentMethods);
      
      // Try to get current subscription (may return 404 if no active subscription)
      try {
        const subscriptionData = await apiService.getCurrentSubscription();
        // Backend already returns camelCase, just map plan details
        setCurrentSubscription({
          ...subscriptionData,
          plan: subscriptionData.planDetails || subscriptionData.plan,
        });
      } catch (subscriptionError: any) {
        // If no current subscription, that's fine - just set to null
        if (subscriptionError.status === 404) {
          setCurrentSubscription(null);
        } else {
          // Only show error for non-404 errors
          setError(subscriptionError.message || 'Failed to load subscription data');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePurchasePlan = async (plan: SubscriptionPlan) => {
    // Show payment method selection modal
    setSelectedPlan(plan);
    setSelectedPaymentMethod(null);
    setShowPaymentMethodSelection(true);
    setShowOfflineModal(true);
  };

  const handleOnlinePayment = async (plan: SubscriptionPlan) => {
    try {
      setProcessingPayment(plan.id);
      setError(null);

      // Create Razorpay order
      const orderData = await apiService.createPaymentOrder({
        subscription_plan_id: plan.id
      });

      console.log('Order data received:', orderData);

      // Check if Razorpay key is available
      const razorpayKey = process.env.REACT_APP_RAZORPAY_KEY_ID;
      console.log('Razorpay key:', razorpayKey ? 'Present' : 'Missing');
      
      if (!razorpayKey) {
        throw new Error('Razorpay key is not configured. Please check your environment variables.');
      }

      // Initialize Razorpay with enhanced options
      const options = {
        key: razorpayKey,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'IntFinder',
        description: orderData.description,
        order_id: orderData.id,
        handler: async (response: any) => {
          try {
            // Verify payment
            await apiService.verifyPayment({
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature
            });

            // Show success message
            showToast(`Payment successful! ${plan.intCoins} Int Coins have been added to your account.`, 'success');
            
            // Refresh data
            await fetchData();
            
            // Refresh the page
            window.location.reload();
          } catch (err: any) {
            const errorMessage = err.message || 'Payment verification failed';
            setError(errorMessage);
            showToast(errorMessage, 'error');
          }
        },
        prefill: {
          name: user ? `${user.firstName} ${user.lastName}`.trim() : 'User',
          email: user?.email || 'user@example.com'
        },
        theme: {
          color: '#4F46E5',
          backdrop_color: '#000000'
        },
        modal: {
          ondismiss: () => {
            setProcessingPayment(null);
            showToast('Payment cancelled', 'info');
          }
        },
        notes: {
          plan_name: plan.name || '',
          int_coins: (plan.intCoins || 0).toString(),
          user_id: (user?.id || '').toString()
        }
      };

      console.log('Razorpay options:', options);

      const razorpay = new (window as any).Razorpay(options);
      razorpay.open();
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to create payment order';
      setError(errorMessage);
      showToast(errorMessage, 'error');
      setProcessingPayment(null);
    }
  };

  const handleOfflinePayment = async () => {
    if (!selectedPlan || !selectedPaymentMethod) return;

    try {
      setSubmittingOffline(true);
      setError(null);

      const offlineData = {
        payment_data: {
          subscription_plan: selectedPlan.id,
          amount: selectedPlan.price,
          payment_method: selectedPaymentMethod.id,
          description: `Offline payment for ${selectedPlan.name}`
        },
        reference_number: offlineFormData.referenceNumber,
        bank_details: JSON.stringify({ details: offlineFormData.bankDetails }),
        payment_instructions: offlineFormData.paymentInstructions,
        payment_proof: offlineFormData.paymentProof
      };

      await apiService.createOfflinePaymentRequest(offlineData);

      // Close modal and refresh data
      setShowOfflineModal(false);
      setSelectedPlan(null);
      setSelectedPaymentMethod(null);
      setOfflineFormData({
        referenceNumber: '',
        bankDetails: '',
        paymentInstructions: '',
        paymentProof: null
      });

      // Show success message
      setError(null);
      showToast('Offline payment request submitted successfully! Our team will review it shortly.', 'success');
      await fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to submit offline payment request');
    } finally {
      setSubmittingOffline(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setOfflineFormData(prev => ({
      ...prev,
      paymentProof: file
    }));
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Invalid Date';
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner text="Loading subscription plans..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Subscription Plans"
        subtitle="Unlock powerful OSINT capabilities with our flexible subscription plans"
        align="center"
      />
      
      {/* Benefits Section */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-4 sm:p-6 lg:p-8 mb-6 sm:mb-8 border border-transparent dark:border-gray-700">
        <div className="text-center mb-6 sm:mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">Why Choose IntFinder?</h2>
          <p className="text-base sm:text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto px-4">
            Join thousands of security professionals, researchers, and investigators who trust IntFinder for their OSINT needs.
          </p>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900/40 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Lightning Fast</h3>
            <p className="text-gray-600 dark:text-gray-300">Get instant results with our optimized search algorithms and global API network.</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/40 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Secure & Reliable</h3>
            <p className="text-gray-600 dark:text-gray-300">Enterprise-grade security with 99.9% uptime and encrypted data transmission.</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/40 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Expert Support</h3>
            <p className="text-gray-600 dark:text-gray-300">24/7 support from our team of OSINT experts and security professionals.</p>
          </div>
        </div>
      </div>

      {/* User Info & Current Balance */}
      {user && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          <Card>
            <div className="p-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white">Welcome back!</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">{user.firstName} {user.lastName}</p>
                </div>
              </div>
            </div>
          </Card>
          
          <Card>
            <div className="p-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white">Current Balance</h3>
                  <p className="text-lg font-bold text-yellow-600 dark:text-yellow-400">{user.intCoinsBalance || 0} Int Coins</p>
                </div>
              </div>
            </div>
          </Card>
          
          <Card>
            <div className="p-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white">Account Status</h3>
                  <Badge variant={user.verificationStatus === 'verified' ? 'success' : 'warning'}>
                    {user.verificationStatus || 'Pending'}
                  </Badge>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        </div>
      )}

      {/* Current Subscription */}
      {currentSubscription && (
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-indigo-950/40 border-blue-200 dark:border-blue-800">
          <div className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 gap-3">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">Current Subscription</h3>
              <Badge variant={currentSubscription.isActive ? 'success' : 'warning'} className="text-xs sm:text-sm px-2 sm:px-3 py-1 w-fit">
                {currentSubscription.isActive ? 'Active' : currentSubscription.status}
              </Badge>
            </div>
            
            <div className="bg-white dark:bg-gray-800/90 rounded-xl shadow-sm border border-blue-100 dark:border-blue-900/40 p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-4">
                <div className="flex items-center space-x-3 sm:space-x-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
                    <span className="text-lg sm:text-xl font-bold text-white">P</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white truncate">
                      {currentSubscription.plan.name}
                    </h4>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 break-words">
                      {formatDate(currentSubscription.startDate)} - {formatDate(currentSubscription.endDate)}
                    </p>
                  </div>
                </div>
                <div className="text-center sm:text-right">
                  <div className="text-xl sm:text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                    {currentSubscription.plan.intCoins}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Int Coins</div>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center justify-center sm:justify-start space-x-6">
                  <div className="text-center">
                    <div className="text-xl sm:text-2xl font-bold text-green-600 dark:text-green-400">
                      {currentSubscription.daysRemaining}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Days Remaining</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl sm:text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {currentSubscription.plan.durationDays}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Total Days</div>
                  </div>
                </div>
                
                <div className="w-full sm:w-32 bg-gray-200 rounded-full h-2 mx-auto sm:mx-0">
                  <div 
                    className="bg-gradient-to-r from-green-400 to-blue-500 h-2 rounded-full transition-all duration-300"
                    style={{ 
                      width: `${Math.max(0, (currentSubscription.daysRemaining / currentSubscription.plan.durationDays) * 100)}%` 
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Available Plans */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
        {plans.map((plan, index) => (
          <Card key={plan.id} className={`relative transition-all duration-300 hover:shadow-xl transform hover:-translate-y-1 ${
            currentSubscription?.plan.id === plan.id && currentSubscription.isActive 
              ? 'ring-2 ring-green-500 bg-gradient-to-br from-green-50 to-green-100' 
              : index === 1 ? 'ring-2 ring-indigo-500 bg-gradient-to-br from-indigo-50 to-indigo-100 sm:scale-105' 
              : 'hover:ring-2 hover:ring-indigo-500 bg-white dark:bg-gray-800'
          }`}>
            {currentSubscription?.plan.id === plan.id && currentSubscription.isActive && (
              <div className="absolute top-2 right-2 sm:top-4 sm:right-4">
                <Badge variant="success" className="animate-pulse text-xs sm:text-sm">Current Plan</Badge>
              </div>
            )}
            
            {index === 1 && !currentSubscription?.isActive && (
              <div className="absolute top-2 right-2 sm:top-4 sm:right-4">
                <Badge variant="info" className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white text-xs sm:text-sm">
                  Most Popular
                </Badge>
              </div>
            )}
            
            <div className="p-4 sm:p-6 lg:p-8">
              <div className="text-center mb-6 sm:mb-8">
                <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center">
                  <span className="text-lg sm:text-2xl font-bold text-white">
                    {index === 0 ? 'B' : index === 1 ? 'P' : 'E'}
                  </span>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">{plan.name}</h3>
                <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-indigo-600 dark:text-indigo-400 mb-2">
                  {formatCurrency(plan.price)}
                </div>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-3 sm:mb-4">per {plan.durationDays} days</p>
                <div className="inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-white rounded-full text-xs sm:text-sm font-semibold">
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"/>
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd"/>
                  </svg>
                  {plan.intCoins} Int Coins
                </div>
              </div>

              <div className="mb-6 sm:mb-8">
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 mb-4 sm:mb-6 text-center px-2">{plan.description}</p>
                
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex items-center p-2 sm:p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                      </svg>
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white">
                        {plan.intCoins} Int Coins
                      </span>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Instant credit to your account</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center p-2 sm:p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white">
                        {plan.durationDays} days access
                      </span>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Full platform access</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center p-2 sm:p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white">
                        {plan.intCoinsExpirationDays} days validity
                      </span>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Coins don't expire immediately</p>
                    </div>
                  </div>
                </div>

                {plan.features.length > 0 && (
                  <div className="mt-4 sm:mt-6">
                    <h4 className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4 text-center">What's Included:</h4>
                    <ul className="space-y-2 sm:space-y-3">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="flex items-start">
                          <div className="w-4 h-4 sm:w-5 sm:h-5 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mr-2 sm:mr-3 mt-0.5 flex-shrink-0">
                            <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-200 leading-relaxed">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="space-y-3 sm:space-y-4">
                <Button
                  variant={index === 1 ? "primary" : "primary"}
                  className={`w-full py-2.5 sm:py-3 text-sm sm:text-base lg:text-lg font-semibold transition-all duration-200 ${
                    index === 1 
                      ? 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 shadow-lg hover:shadow-xl' 
                      : 'bg-indigo-600 hover:bg-indigo-700 shadow-md hover:shadow-lg'
                  }`}
                  disabled={
                    processingPayment === plan.id ||
                    (currentSubscription?.plan.id === plan.id && currentSubscription.isActive)
                  }
                  onClick={() => handlePurchasePlan(plan)}
                >
                  {processingPayment === plan.id ? (
                    <div className="flex items-center justify-center">
                      <LoadingSpinner text="Processing..." />
                    </div>
                  ) : currentSubscription?.plan.id === plan.id && currentSubscription.isActive ? (
                    <div className="flex items-center justify-center">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="hidden sm:inline">Current Plan</span>
                      <span className="sm:hidden">Current</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                      </svg>
                      <span className="hidden sm:inline">Get Started - {formatCurrency(plan.price)}</span>
                      <span className="sm:hidden">Get Started</span>
                    </div>
                  )}
                </Button>
                
                {!currentSubscription?.isActive && (
                  <div className="text-center space-y-2">
                    <div className="flex items-center justify-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                      <div className="flex items-center">
                        <svg className="w-4 h-4 mr-1 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                        </svg>
                        Secure Payment
                      </div>
                      <div className="flex items-center">
                        <svg className="w-4 h-4 mr-1 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.293l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z" clipRule="evenodd" />
                        </svg>
                        Instant Activation
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                      Powered by Razorpay • 24/7 Support
                    </p>
                  </div>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      {plans.length === 0 && (
        <Card>
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No subscription plans available</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Please check back later for available plans.</p>
          </div>
        </Card>
      )}

      {/* Payment Method Selection Modal */}
      {showOfflineModal && selectedPlan && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50 p-4">
          <div className="relative w-full max-w-md max-h-full">
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-lg">
              <div className="flex justify-between items-start p-4 sm:p-6 rounded-t">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 pr-4">
                  {showPaymentMethodSelection ? 'Select Payment Method' : 'Complete Payment for ' + selectedPlan.name}
                </h3>
                <button
                  type="button"
                  className="text-gray-400 dark:text-gray-500 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center dark:hover:bg-gray-600 dark:hover:text-white"
                  onClick={() => {
                    setShowOfflineModal(false);
                    setSelectedPlan(null);
                    setSelectedPaymentMethod(null);
                    setShowPaymentMethodSelection(true);
                    setOfflineFormData({
                      referenceNumber: '',
                      bankDetails: '',
                      paymentInstructions: '',
                      paymentProof: null
                    });
                  }}
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
              <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
                {showPaymentMethodSelection ? (
                  // Payment Method Selection
                  <div className="space-y-3 sm:space-y-4">
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 sm:p-4">
                      <h4 className="font-medium text-blue-900 mb-2 text-sm sm:text-base">Plan Details</h4>
                      <p className="text-xs sm:text-sm text-blue-700 dark:text-blue-300">
                        <strong>{selectedPlan.name}</strong> - {formatCurrency(selectedPlan.price)}
                      </p>
                      <p className="text-xs sm:text-sm text-blue-600 dark:text-blue-400">
                        You'll receive <strong>{selectedPlan.intCoins} Int Coins</strong> valid for {selectedPlan.intCoinsExpirationDays} days
                      </p>
                    </div>
                    
                    <p className="text-xs sm:text-sm text-gray-600">
                      Choose your preferred payment method:
                    </p>
                    
                    <div className="space-y-2 sm:space-y-3">
                      {paymentMethods.map(method => (
                        <div
                          key={method.id}
                          className={`p-3 sm:p-4 border rounded-lg cursor-pointer transition-all duration-200 ${
                            selectedPaymentMethod?.id === method.id
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-2 ring-blue-200'
                              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                          onClick={() => setSelectedPaymentMethod(method)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                              <div className="flex-shrink-0">
                                {method.isOnline ? (
                                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                                    <svg className="w-4 h-4 sm:w-6 sm:h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                    </svg>
                                  </div>
                                ) : (
                                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                                    <svg className="w-4 h-4 sm:w-6 sm:h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                  </div>
                                )}
                              </div>
                              <div className="min-w-0 flex-1">
                                <h4 className="font-medium text-gray-900 text-sm sm:text-base truncate">{method.displayName}</h4>
                                <p className="text-xs sm:text-sm text-gray-600 truncate">{method.description}</p>
                                {method.isOnline && (
                                  <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                                    ⚡ Instant payment & activation
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0">
                              <Badge variant={method.isOnline ? 'success' : 'warning'} className="text-xs">
                                {method.isOnline ? 'Online' : 'Offline'}
                              </Badge>
                              {selectedPaymentMethod?.id === method.id && (
                                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-2 pt-4">
                      <Button variant="secondary" onClick={() => setShowOfflineModal(false)} className="w-full sm:w-auto">
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        disabled={!selectedPaymentMethod}
                        onClick={() => {
                          if (selectedPaymentMethod?.isOnline) {
                            // Proceed with online payment
                            handleOnlinePayment(selectedPlan);
                            setShowOfflineModal(false);
                          } else {
                            // Show offline payment form
                            setShowPaymentMethodSelection(false);
                          }
                        }}
                        className="w-full sm:w-auto"
                      >
                        Continue
                      </Button>
                    </div>
                  </div>
                ) : (
                  // Offline Payment Form
                  <div className="space-y-3 sm:space-y-4">
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 sm:p-4">
                      <div className="flex items-start">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400 mr-2 sm:mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        <p className="text-xs sm:text-sm text-yellow-700 dark:text-yellow-300">
                          You selected offline payment. Please provide the required details below.
                        </p>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="referenceNumber" className="block text-xs sm:text-sm font-medium text-gray-900 mb-1">
                        Reference Number *
                      </label>
                      <input
                        type="text"
                        id="referenceNumber"
                        value={offlineFormData.referenceNumber}
                        onChange={(e) => setOfflineFormData(prev => ({ ...prev, referenceNumber: e.target.value }))}
                        className="bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 text-gray-900 text-xs sm:text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2 sm:p-2.5"
                        placeholder="Enter reference number"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="bankDetails" className="block text-xs sm:text-sm font-medium text-gray-900 mb-1">
                        Bank Details
                      </label>
                      <textarea
                        id="bankDetails"
                        value={offlineFormData.bankDetails}
                        onChange={(e) => setOfflineFormData(prev => ({ ...prev, bankDetails: e.target.value }))}
                        className="bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 text-gray-900 text-xs sm:text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2 sm:p-2.5"
                        placeholder="Enter bank details (if applicable)"
                        rows={3}
                      />
                    </div>

                    <div>
                      <label htmlFor="paymentInstructions" className="block text-xs sm:text-sm font-medium text-gray-900 mb-1">
                        Payment Instructions
                      </label>
                      <textarea
                        id="paymentInstructions"
                        value={offlineFormData.paymentInstructions}
                        onChange={(e) => setOfflineFormData(prev => ({ ...prev, paymentInstructions: e.target.value }))}
                        className="bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 text-gray-900 text-xs sm:text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2 sm:p-2.5"
                        placeholder="Enter payment instructions"
                        rows={3}
                      />
                    </div>

                    <div>
                      <label htmlFor="paymentProof" className="block text-xs sm:text-sm font-medium text-gray-900 mb-1">
                        Payment Proof (Optional)
                      </label>
                      <div className="mt-1">
                        <div className="flex items-center justify-center w-full">
                          <label htmlFor="paymentProof" className="flex flex-col items-center justify-center w-full h-24 sm:h-32 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800">
                            <div className="flex flex-col items-center justify-center pt-3 pb-4 sm:pt-5 sm:pb-6 px-2">
                              {offlineFormData.paymentProof ? (
                                <div className="text-center">
                                  <svg className="w-6 h-6 sm:w-8 sm:h-8 mb-2 sm:mb-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  <p className="mb-1 sm:mb-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                                    <span className="font-semibold truncate block">{offlineFormData.paymentProof.name}</span>
                                  </p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">
                                    {(offlineFormData.paymentProof.size / 1024 / 1024).toFixed(2)} MB
                                  </p>
                                </div>
                              ) : (
                                <div className="text-center">
                                  <svg className="w-6 h-6 sm:w-8 sm:h-8 mb-2 sm:mb-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                  </svg>
                                  <p className="mb-1 sm:mb-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                                    <span className="font-semibold">Click to upload</span>
                                  </p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                                    Screenshot, receipt, or payment confirmation (MAX. 10MB)
                                  </p>
                                </div>
                              )}
                            </div>
                            <input
                              id="paymentProof"
                              type="file"
                              className="hidden"
                              onChange={handleFileChange}
                              accept="image/*,.pdf,.doc,.docx"
                            />
                          </label>
                        </div>
                        {offlineFormData.paymentProof && (
                          <div className="mt-2 flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-2">
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => setOfflineFormData(prev => ({ ...prev, paymentProof: null }))}
                              className="text-xs"
                            >
                              Remove File
                            </Button>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              Supported: JPG, PNG, PDF, DOC, DOCX
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-2 pt-4">
                      <Button 
                        variant="secondary" 
                        onClick={() => setShowPaymentMethodSelection(true)}
                        className="w-full sm:w-auto"
                      >
                        Back
                      </Button>
                      <Button 
                        variant="primary" 
                        onClick={handleOfflinePayment} 
                        disabled={submittingOffline || !offlineFormData.referenceNumber}
                        className="w-full sm:w-auto"
                      >
                        {submittingOffline ? <LoadingSpinner text="Submitting..." /> : 'Submit Offline Payment'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}; 